import {AfterViewInit, Directive, Input, OnDestroy, OnInit} from '@angular/core';
import {Subscription} from 'rxjs';
import {MatSelect} from '@angular/material/select';
import {MatOption, MatOptionSelectionChange} from '@angular/material/core';
import {FormControl} from '@angular/forms';

export interface AppSelectElementInterface {
    isSelected(): boolean;

    getLabel(): string;

    setFiltered(value: boolean): void;

    isFiltered(): boolean;

    update(): void;
}

export interface AppSelectOptionElementInterface extends AppSelectElementInterface {
    setSelected(selected: boolean): void;

    getGroup(): AppSelectElementInterface | undefined;

    subscribe(fn: () => void): Subscription;
}

export interface AppSelectCountsInterface {
    groups: number;
    options: number;
}

@Directive({
    selector: '[appSelect]',
})
export class AppSelectElementDirective implements OnInit, AfterViewInit, OnDestroy {
    readonly #matSelect: MatSelect;

    readonly #elements: AppSelectElementInterface[] = [];

    readonly #subscriptions: Subscription[] = [];

    @Input()
    readonly appSelect!: FormControl;

    constructor(matSelect: MatSelect) {
        this.#matSelect = matSelect;

        // Ugly solution to fix an issue which we can't prevent.
        matSelect._handleKeydown = (event: KeyboardEvent) => event.stopPropagation();
    }

    ngOnInit(): void {
        if (undefined === this.appSelect) {
            throw Error('Unable to load select directive, form control is required');
        }

        this.#subscriptions.push(this.#matSelect.optionSelectionChanges.subscribe((event: MatOptionSelectionChange) => this.#handleOptionSelectionChanges(event)));
    }

    ngOnDestroy(): void {
        this.#subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
    }

    ngAfterViewInit(): void {
        if (undefined !== this.#matSelect.customTrigger) {
            const selected = this.appSelect.value as (number | string)[];

            // TODO BD-2397: Find a better way to bubble selected values set in FormControl for reactive forms.
            this.#matSelect.options.forEach((item: MatOption) => {
                if (selected.includes(item.value)) {
                    item.select();
                }
            });
        }
    }

    /**
     * First we'll process on options, and when we're done, process groups (if there are any) since they're based on their children
     */
    filter(value: string): void {
        this.#elements.forEach(element => this.#handleElementFilteredState(element, value));
        this.#elements.forEach(element => element.update());
    }

    register(directive: AppSelectElementInterface): void {
        this.#elements.push(directive);
    }

    getCounts(): AppSelectCountsInterface {
        const groups = this.#elements.filter((element: AppSelectElementInterface) => !this.#isOptionElement(element) && element.isSelected()).length;
        const options = this.#elements.filter((element: AppSelectElementInterface) => this.#isOptionElement(element) && element.isSelected()).length

        return {options, groups};
    }

    #handleElementFilteredState(element: AppSelectElementInterface, value: string) {
        const isFiltered = '' !== value && !element.getLabel().toLowerCase().includes(value.toLowerCase(), 0);

        element.setFiltered(isFiltered);
    }

    #handleOptionSelectionChanges(event: MatOptionSelectionChange): void {
        const optionValue: number | string = event.source.value;

        this.#matSelect.options
            .filter((matOption: MatOption) => matOption.value === optionValue)
            .forEach((matOption: MatOption) => event.source.selected ? matOption.select() : matOption.deselect());
    }

    #isOptionElement(option: AppSelectElementInterface | AppSelectOptionElementInterface): option is AppSelectOptionElementInterface {
        return 'getGroup' in option;
    }
}
